import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  VOICE_MAX_RECORDING_SECONDS,
  isVoiceInputSupportedLocale,
  isVoiceRecordingSupported,
  pickRecordingMimeType,
} from '@/constants/voice';
import useAnalytics from '@/hooks/useAnalytics';
import useApi from '@/hooks/useApi';
import { TranscribeError, transcribeAudio } from '@/services/transcribe';

export type VoiceInputStatus = 'idle' | 'recording' | 'transcribing';

/** Why voice input stopped short, in terms the composer can explain. */
export type VoiceInputError =
  | 'permission_denied'
  | 'no_speech'
  | 'too_long'
  | 'unsupported'
  | 'rate_limited'
  | 'network'
  | 'server_error';

interface UseVoiceInputOptions {
  /**
   * Receives the finished transcript. `autoSend` reflects which control ended
   * the recording: the stop button asks for review in the composer, the send
   * button asks to send immediately.
   */
  onTranscript: (text: string, options: { autoSend: boolean }) => void;
  /** Where the composer is mounted, for analytics segmentation. */
  entryPoint: 'landing' | 'conversation';
}

export function useVoiceInput({ onTranscript, entryPoint }: UseVoiceInputOptions) {
  const { t, i18n } = useTranslation('chat');
  const analytics = useAnalytics();
  const query = useApi();

  const [status, setStatus] = useState<VoiceInputStatus>('idle');
  const [error, setError] = useState<VoiceInputError | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const meterFrameRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);
  const autoSendRef = useRef(false);

  const isAvailable = useMemo(
    () => isVoiceRecordingSupported() && isVoiceInputSupportedLocale(i18n.language),
    [i18n.language]
  );

  const remainingSeconds = Math.max(0, VOICE_MAX_RECORDING_SECONDS - Math.floor(elapsedMs / 1000));

  const clearError = useCallback(() => setError(null), []);

  /** Release the mic, the meter loop, and the audio graph. */
  const teardown = useCallback(() => {
    if (meterFrameRef.current !== null) {
      cancelAnimationFrame(meterFrameRef.current);
      meterFrameRef.current = null;
    }
    // Stopping every track is what turns off the browser's recording
    // indicator; leaving it on reads as VoteMate still listening.
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    recorderRef.current = null;
    setLevel(0);
  }, []);

  const upload = useCallback(
    async (blob: Blob, autoSend: boolean) => {
      setStatus('transcribing');
      const elapsedSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
      const startedTranscribingAt = Date.now();
      try {
        const { text } = await transcribeAudio(blob, query);
        analytics.track('Voice Input Completed', {
          duration_seconds: elapsedSeconds,
          transcript_length_characters: text.length,
          transcript_length_words: text.split(/\s+/).length,
          latency_ms: Date.now() - startedTranscribingAt,
          locale: i18n.language,
          auto_sent: autoSend,
          entry_point: entryPoint,
        });
        setStatus('idle');
        onTranscript(text, { autoSend });
      } catch (err) {
        const kind: VoiceInputError = err instanceof TranscribeError ? err.kind : 'server_error';
        analytics.track('Voice Input Failed', {
          error_type: kind,
          duration_seconds: elapsedSeconds,
          entry_point: entryPoint,
          locale: i18n.language,
        });
        setError(kind);
        setStatus('idle');
      }
    },
    [analytics, entryPoint, i18n.language, onTranscript, query]
  );

  // `onstop` fires once, long after the render that registered it, and
  // `useApi` hands back a new function every render — so the handler reaches
  // the current uploader through a ref rather than closing over one.
  const uploadRef = useRef(upload);
  useEffect(() => {
    uploadRef.current = upload;
  }, [upload]);

  const start = useCallback(async () => {
    setError(null);
    const mimeType = pickRecordingMimeType();
    if (!mimeType) {
      setError('unsupported');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Covers both an explicit denial and a browser/iframe that never offered
      // the prompt; the voter's next step is the same either way.
      analytics.track('Voice Input Failed', {
        error_type: 'permission_denied',
        duration_seconds: 0,
        entry_point: entryPoint,
        locale: i18n.language,
      });
      setError('permission_denied');
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    cancelledRef.current = false;
    autoSendRef.current = false;
    startedAtRef.current = Date.now();
    setElapsedMs(0);

    const recorder = new MediaRecorder(stream, { mimeType });
    recorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      teardown();
      if (cancelledRef.current) return;
      void uploadRef.current(blob, autoSendRef.current);
    };
    recorder.start();
    setStatus('recording');

    // Live input level for the waveform. An analyser on the same stream costs
    // nothing extra and, unlike a fixed animation, actually tells the voter
    // the mic is hearing them.
    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sumOfSquares = 0;
        for (let i = 0; i < samples.length; i += 1) {
          const centered = (samples[i] - 128) / 128;
          sumOfSquares += centered * centered;
        }
        // RMS of a spoken syllable sits near 0.1, so the x4 spends the bar's
        // range on the levels a voice actually produces.
        setLevel(Math.min(1, Math.sqrt(sumOfSquares / samples.length) * 4));
        meterFrameRef.current = requestAnimationFrame(tick);
      };
      meterFrameRef.current = requestAnimationFrame(tick);
    } catch {
      // A missing AudioContext costs the waveform, not the recording.
    }

    analytics.track('Voice Input Started', {
      entry_point: entryPoint,
      locale: i18n.language,
    });
  }, [analytics, entryPoint, i18n.language, teardown]);

  // `status` stays `recording` until the asynchronous `onstop`, so a double
  // tap or the cap timer can land here twice. `stop()` on an inactive
  // recorder throws, and a second call would also overwrite which control
  // ended the recording — so only the first call while recording counts.
  const finish = useCallback((autoSend: boolean) => {
    const recorder = recorderRef.current;
    if (recorder?.state !== 'recording') return;
    autoSendRef.current = autoSend;
    recorder.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const elapsedSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setStatus('idle');
    setError(null);
    analytics.track('Voice Input Cancelled', {
      duration_seconds: elapsedSeconds,
      entry_point: entryPoint,
    });
  }, [analytics, entryPoint]);

  const stopAndInsert = useCallback(() => finish(false), [finish]);
  const stopAndSend = useCallback(() => finish(true), [finish]);

  // Elapsed time and the hard stop at the cap. The timeout always ends in the
  // review path: a recording cut off by a timer is the least likely to be
  // worth sending unread.
  useEffect(() => {
    if (status !== 'recording') return undefined;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= VOICE_MAX_RECORDING_SECONDS * 1000) finish(false);
    }, 200);
    return () => clearInterval(id);
  }, [status, finish]);

  // Navigating away mid-recording must not leave the mic hot.
  useEffect(() => teardown, [teardown]);

  const errorMessage = error ? t(`input.mic.errors.${error}`) : null;

  return useMemo(
    () => ({
      status,
      isRecording: status === 'recording',
      isTranscribing: status === 'transcribing',
      isAvailable,
      remainingSeconds,
      level,
      error: errorMessage,
      clearError,
      start,
      stopAndInsert,
      stopAndSend,
      cancel,
    }),
    [
      status,
      isAvailable,
      remainingSeconds,
      level,
      errorMessage,
      clearError,
      start,
      stopAndInsert,
      stopAndSend,
      cancel,
    ]
  );
}
